import { Head } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { LeaderboardView } from "@/components/leaderboard/leaderboard-view"
import AppLayout from "@/layouts/app-layout"
import { crewLeaderboardPath } from "@/routes"
import type { LeaderboardPeriod, LeaderboardProps } from "@/types/leaderboard"

/**
 * OKS-51 — Brand/Crew/Leaderboard.tsx (`/crew/leaderboard`).
 *
 * Brand admin surface inside the Crew section of the sidebar nav.
 * Same content as the creator surface (`/leaderboard`) minus the
 * self-row pin — admins / agencies are not on the leaderboard (UX
 * Spec §2.2 lock; `self_rank: null` by construction from
 * `BrandLeaderboardController`).
 *
 * # Sidebar IA placement
 *
 * Sits in the Crew section as a sibling to Roster + Segments +
 * Messages. Sidebar entry added in `brand-sidebar.tsx`.
 *
 * # Brand isolation
 *
 * Identical contract to the creator surface — `Current.brand`
 * resolved upstream from the session, no `:brand_id` URL param. A
 * brand admin of Brand B who hand-types `/crew/leaderboard` while in
 * Brand B's context gets Brand B's ranking; no URL construction can
 * surface Brand A's data (PRD AC §11).
 */
export default function BrandCrewLeaderboard(props: LeaderboardProps) {
  const { t } = useTranslation()

  return (
    <AppLayout>
      <Head title={t("leaderboard.title")} />

      <main className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-6">
        <LeaderboardView
          {...props}
          audience="admin"
          routeHelper={
            crewLeaderboardPath as (o: { period: LeaderboardPeriod }) => string
          }
        />
      </main>
    </AppLayout>
  )
}
